import {UserPermissions, UserPermissionFlags, DEFAULT_PERMISSION_FLAGS, AdminAction} from "@/lib/types";
import {currentUser} from "@/firebase/firebaseAuth";
import {doc, DocumentSnapshot, getDoc, collection, query, where, orderBy, limit, getDocs} from "firebase/firestore";
import {firestore, functions, httpsCallable} from "@/firebase/firebaseConfig";

/**
 * Read the current signed-in user's permissions via direct Firestore read.
 * Used by AuthContext on login / page load -- NOT the admin callable.
 */
export function getUserPermissions(): Promise<UserPermissions | null> {
    const user = currentUser();

    if (!user) {
        return Promise.resolve(null);
    }

    return getUserPermissionsByUid(user.uid);
}

/**
 * Direct Firestore read of a user's permissions doc.
 * Still used by AuthContext for the current user's own permissions.
 * Admin code should prefer `adminGetUserPermissions` instead.
 */
export function getUserPermissionsByUid(uid: string): Promise<UserPermissions | null> {
    if (!uid) {
        return Promise.resolve(null);
    }

    return getDoc(doc(firestore, "UserPermissions", uid)).then((snapshot) => {
        return mapUserPermissions(snapshot);
    });
}

// ---------------------------------------------------------------------------
// Admin callable wrappers (require caller to be admin)
// ---------------------------------------------------------------------------

/**
 * Fetch any user's permissions via the admin callable.
 * Returns null if the target user has no UserPermissions doc.
 */
export async function adminGetUserPermissions(targetUid: string): Promise<UserPermissions | null> {
    if (!targetUid) return null;

    const callable = httpsCallable(functions, "user-adminGetUserPermissions");
    const {data} = await callable({targetUid});

    if (data === null || data === undefined) return null;

    const d = data as any;
    return {
        uid: d.uid,
        admin: !!d.admin,
        betaProgram: !!d.betaProgram,
        dubbing: !!d.dubbing,
        upload: !!d.upload,
        dateCreated: d.dateCreated ? new Date(d.dateCreated) : new Date(),
        lastUpdated: d.lastUpdated ? new Date(d.lastUpdated) : new Date(),
    };
}

/**
 * Create or overwrite a user's permissions via the admin callable.
 * All four flag fields are required (full overwrite).
 * Returns the updated UserPermissions object.
 */
export async function adminSetUserPermissions(
    targetUid: string,
    permissions: UserPermissionFlags
): Promise<UserPermissions> {
    if (!targetUid) {
        throw new Error("targetUid is required");
    }

    const callable = httpsCallable(functions, "user-adminSetUserPermissions");
    const {data} = await callable({targetUid, permissions});

    const d = data as any;
    return {
        uid: d.uid,
        admin: !!d.admin,
        betaProgram: !!d.betaProgram,
        dubbing: !!d.dubbing,
        upload: !!d.upload,
        dateCreated: d.dateCreated ? new Date(d.dateCreated) : new Date(),
        lastUpdated: d.lastUpdated ? new Date(d.lastUpdated) : new Date(),
    };
}

/**
 * Build a full UserPermissionFlags object from the current state
 * (or defaults when the user has no doc yet), with overrides applied.
 */
export function buildPermissionFlags(
    current: UserPermissions | null,
    overrides: Partial<UserPermissionFlags>
): UserPermissionFlags {
    const base: UserPermissionFlags = current
        ? {admin: current.admin, betaProgram: current.betaProgram, dubbing: current.dubbing, upload: current.upload}
        : {...DEFAULT_PERMISSION_FLAGS};
    return {...base, ...overrides};
}

// ---------------------------------------------------------------------------
// Legacy helpers (kept for backward compat with existing non-admin callers)
// ---------------------------------------------------------------------------

export async function setUserPermissions(
    uid: string,
    permissions: Partial<UserPermissions>
): Promise<void> {
    if (!uid) {
        return Promise.reject(new Error("UID is required to update permissions"));
    }

    const setPermissions = httpsCallable(functions, "admin-setUserPermissions");
    await setPermissions({uid, permissions});
}

export function setAdminPermission(uid: string, admin: boolean): Promise<void> {
    return setUserPermissions(uid, {admin});
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function mapUserPermissions(docSnap: DocumentSnapshot): UserPermissions {
    if (!docSnap.exists()) {
        return {
            admin: false,
            betaProgram: true,
            dubbing: true,
            upload: true,
            dateCreated: new Date(),
            lastUpdated: new Date(),
        };
    }

    const data = docSnap.data();
    return {
        ...data,
        admin: !!data?.admin,
        betaProgram: data?.betaProgram ?? true,
        dubbing: data?.dubbing ?? true,
        upload: data?.upload ?? true,
        dateCreated: data.dateCreated ? data.dateCreated.toDate() : new Date(),
        lastUpdated: data.lastUpdated ? data.lastUpdated.toDate() : new Date(),
    } as UserPermissions;
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export async function getAdminActionsForUser(uid: string): Promise<AdminAction[]> {
    if (!uid) return [];

    const q = query(
        collection(firestore, "AdminActions"),
        where("targetUid", "==", uid),
        orderBy("timestamp", "desc"),
        limit(50)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            action: data.action ?? '',
            performedBy: data.performedBy ?? '',
            performedByEmail: data.performedByEmail,
            targetUid: data.targetUid ?? '',
            changes: data.changes ?? {},
            timestamp: data.timestamp?.toDate?.() ?? new Date(),
        };
    });
}
